<script>
  import { afterUpdate } from 'svelte';
  import { Props } from 'web-adapter-js';
  import { configCountry } from '../../../util/url-builder';
  import { getBanners } from './scripts/services';

  /************************************************
   * PROPERTIES
   ************************************************/
  export let key = '';
  export let data = {
    title: '',
    sub_title: '',
    store_ids: [],
    store_types: [],
    // MANDATORY
    lat: 0,
    lng: 0,
  };

  const props = new Props();
  const prefix_url = configCountry()['base_img'];

  let isInitialized = false;
  let hasBanners = false;
  let showMoreEnabled = false;
  let slidesEl;
  let lastIndexMoved = 0;
  let canRotateLeft = false;
  let canRotateRight = true;
  let images = [];

  /************************************************
   * METHODS
   ************************************************/
  const fetchData = async () => {
    try {
      const res = await getBanners(data);
      images = res;
      hasBanners = images.length;
      checkArrows();
    } catch (err) {
      console.log(err);
    }
  };

  const rotateLeft = (e) => {
    const items = slidesEl.children;
    // CHECK IF THERE IS ANOTHER ITEM TO BE SHOWN
    if (!canRotateLeft) return;
    lastIndexMoved--;
    scrollSmooth(items, false);
  };

  const rotateRight = (e) => {
    const items = slidesEl.children;
    // CHECK IF THERE IS ANOTHER ITEM TO BE SHOWN
    if (!canRotateRight) return;
    lastIndexMoved++;
    scrollSmooth(items, true);
  };

  const isVisible = (el) => {
    if (!el) return false;
    const bounding = el.getBoundingClientRect();
    const right = window.innerWidth || document.documentElement.clientWidth;
    return bounding.left >= 0 && bounding.right <= right;
  };

  const firstIsVisible = (items) => items.length && isVisible(items[0]);
  const lastIsVisible = (items) =>
    items.length && isVisible(items[items.length - 1]);
  const nextItemNonVisible = (items, index, asc) => {
    if (!items.length) return null;
    if (isVisible(items[index]))
      return nextItemNonVisible(items, asc ? ++index : --index, asc);
    return items[index];
  };

  const scrollSmooth = (items, asc) => {
    let nextItem = nextItemNonVisible(items, lastIndexMoved, asc);
    if (isVisible(nextItem)) return;
    slidesEl.scroll({ left: nextItem.offsetLeft });
  };

  const checkArrows = () => {
    const items = slidesEl && slidesEl.children;
    canRotateLeft = items && !firstIsVisible(items);
    canRotateRight = items && !lastIsVisible(items);
  };

  const _mapData = (externalData) => {
    return {
      ...externalData,
      // PLAIN ARRAY
      store_ids: externalData && externalData.store_ids.map(({ item }) => item),
      store_types:
        externalData && externalData.store_types.map(({ item }) => item),
    };
  };

  /************************************************
   * LIFECYCLE
   ************************************************/
  afterUpdate(async () => {
    const property = props.find(`gema-banners-${key}`);
    if (!!key && property && !isInitialized) {
      isInitialized = true; // NEED THIS TO AVOID RE REQUEST
      data = _mapData(property);
      await fetchData();
    }
  });
</script>

<style type="text/scss">
  @import './src/styles';
  @import './src/scss/abstracts/mixins';
  @import './styles/variables';
  @import './styles/icons';

  .gem-banners {
    margin-bottom: 30px;

    &-title {
      @include font-poppins(30px, 600, normal, normal, 1.53, -1.88px, #332927);
      margin-bottom: 5px;
    }

    &-description {
      @include font-poppins(
        18px,
        normal,
        normal,
        normal,
        1.22,
        normal,
        #706967
      );
    }

    &-container {
      position: relative;
      width: 100%;
    }

    &-navigation {
      background-color: #fff;
      border: none;
      border-radius: 50%;
      box-shadow: 0px 0px 5px rgba($color: black, $alpha: 0.7);
      height: 40px;
      margin-top: -20px;
      position: absolute;
      top: 50%;
      width: 40px;

      &:focus {
        outline: none;
      }

      &.left {
        left: 10px;
      }

      &.right {
        right: 10px;
      }
    }

    &-slides {
      align-items: stretch;
      display: flex;
      justify-content: flex-start;
      flex-wrap: nowrap;
      overflow-x: hidden;
      scroll-behavior: smooth;
      width: 100%;
    }

    &-item {
      margin-right: 30px;

      &:last-child {
        margin-right: 0;
      }

      &-img {
        border-radius: $element-border-radius;
        max-height: 200px;
      }

      &-complementary {
        background-image: $banners-background;
        border-radius: $element-border-radius;
        position: relative;
        min-width: 230px;

        .gem-banners-item-img {
          // RESET STYLES
          border-radius: 0;
          max-height: auto;
          margin: 0;
          transition: transform 0.5s ease;
          pointer-events: none;
          position: absolute;
          top: 0;
          right: 0;
          height: 100%;
          width: auto;
        }

        i {
          position: absolute;
          bottom: 20px;
          right: 20px;
          font-size: $f-size-xl4;
          padding: 5px;
          &::before,
          &::after {
            color: $white;
          }
        }
      }

      &-titles-container {
        padding: 20px 0 0 10px;
        color: $white;

        .title {
          font-family: $heading-font;
          margin-bottom: 10px;
        }

        .subtitle {
          font-family: $text-font;
          font-size: $f-size-s;
        }
      }
    }
  }
</style>

<svelte:options tag="gema-banners" />
<main>
  {#if hasBanners}
    <section class="gem-banners">
      <section>
        <h1 class="gem-banners-title">{data.title}</h1>
        <p class="gem-banners-description">{data.sub_title}</p>
      </section>
      <div class="gem-banners-container">
        <div
          class="gem-banners-slides"
          bind:this={slidesEl}
          on:scroll={checkArrows}
          id="gem-banners-slides">
          {#each images as img (img.id)}
            <div class="gem-banners-item">
              <a
                class="gem-banners-item-link"
                href={img.deep_link}
                target="blank">
                <img
                  class="gem-banners-item-img"
                  src={`${prefix_url}/${img.mobile_image}`}
                  alt={img.name} />
              </a>
            </div>
          {/each}
          {#if showMoreEnabled}
            <div class="gem-banners-item-complementary">
              <img
                class="gem-banners-item-img"
                src="https://images.rappi.com/web/banner_1.svg"
                alt="complementary" />
              <div class="gem-banners-item-titles-container">
                <h3 class="title">TITLE</h3>
                <p class="subtitle">SUBTITLE</p>
              </div>
              <i class="iconf-arrow_right" />
            </div>
          {/if}
        </div>

        {#if images.length > 1}
          {#if canRotateLeft}
            <button class="gem-banners-navigation left" on:click={rotateLeft}>
              <i class="iconm-arrow_left" />
            </button>
          {/if}
          {#if canRotateRight}
            <button class="gem-banners-navigation right" on:click={rotateRight}>
              <i class="iconm-arrow_right" />
            </button>
          {/if}
        {/if}
      </div>
    </section>
  {/if}
</main>
